<script setup>
import { debounce } from '@/composables/helpers/performance';

const props = defineProps({
    page: {
        type: Object,
        required: false,
    },
    headline: {
        type: String,
        required: false,
        default: 'Related Blog Posts',
    },
    button: {
        type: Object,
        required: false,
        default: {},
    },
    blog_posts: {
        type: Array,
        required: false,
        default: [],
    },
    backgroundColor: {
        type: String,
        required: false,
        default: 'var(--Chapati-500)',
    },
    attributes: {
        type: Object,
        required: false
    }
});

const active = ref(0)
const scrollContainer = ref(null)

const query = computed(() => {
    const ids = props.blog_posts?.length ? props.blog_posts.map(item => item.post.id) : []
    const params = { include: ids, limit: 3 }
    if (props.page?.category) params.category = props.page.category
    return params
})

const { data, pending, error, refresh } = await useAsyncData('blogs:featured', () => $fetch('/api/v1/posts', { params: query.value }))

const router = useRouter();

const handleCardClick = (payload) => {
    if (payload.type === 'tag') {
        router.push({ path: '/blog', query: { tags: payload.value.toLowerCase() } });
    }
};

const next = () => {
    active.value = active.value < data.value.length - 1 ? active.value + 1 : 0
    jump(active.value)
}

const previous = () => {
    active.value = active.value > 0 ? active.value - 1 : 0
    jump(active.value)
}

const check = () => {
    if (!scrollContainer.value) return
    const children = scrollContainer.value.children;
    let leftmostElement = null;
    let leftmostPosition = Infinity;
    for (let child of children) {
        const rect = child.getBoundingClientRect();
        const containerRect = scrollContainer.value.getBoundingClientRect();
        if (rect.right > containerRect.left && rect.left < containerRect.right) {
            if (rect.left < leftmostPosition) {
                leftmostPosition = rect.left;
                leftmostElement = child;
            }
        }
    }
    
    if (leftmostElement) {
        active.value = parseInt(leftmostElement.getAttribute('data-index'), 10)
    }
}

const jump = (index) => {
    if (!scrollContainer.value) return
    const target = scrollContainer.value.querySelector(`li[data-index="${index}"]`)
    if (target) {
        active.value = index;
        const offsetLeft = target.offsetLeft - scrollContainer.value.offsetLeft;
        scrollContainer.value.style.scrollSnapType = 'none'
        setTimeout(() => { scrollContainer.value.style.scrollSnapType = 'x mandatory' }, 1000)
        scrollContainer.value.scrollTo({
            left: offsetLeft,
            behavior: 'smooth'
        });
    }
}

const debounceScroll = debounce(check, 100)

onMounted(() => {
    if (process.server) return;
    if (scrollContainer.value) scrollContainer.value.addEventListener('scroll', debounceScroll)
})

onUnmounted(() => {
    if (process.server) return;
    if (scrollContainer.value) scrollContainer.value.removeEventListener('scroll', debounceScroll)
})

</script>

<template>
    <section class="block-blog-three-up" tabindex="-1" :class="{ 'is-post': props.page }">
        <div class="container">
            <div class='header'>
                <MessHtml tag="h2" class="headline" :html="headline" />
                <ProjectButton v-if="Object.keys(button).length" :button="button" class="button" color="white" hoverColor="var(--teal)" outline />
            </div>
            <div class='posts-container' v-if="data && data.length">
                <div class='posts'>
                    <ul ref="scrollContainer">
                        <li v-for="(card, index) in data" :key="card.data.url" :data-index=index>
                            <article>
                                <FragmentsCardsBlogCard class="post" :link="card.data.url" :headline="card.data.title"
                                    :cardImage="card.data.image" :altText="card.data.imageAltText"
                                    :category="card.data.category" :date="card.data.datePublished" :excerpt="card.data.excerpt"
                                    :tags="card.data.tags" @clicked="handleCardClick" tagColor="teal"
                                    size="medium" />
                            </article>
                        </li>
                    </ul>
                </div>
                <div class='controls'>
                    <div class='controller'>
                        <FragmentsCarouselsControlsArrows :total="data.length" :current=active @forward="next(true)" @back="previous(true)" />
                        <FragmentsCarouselsControlsDot color="var(--orange)" :itemTotal="data.length" :activeIndex="active" @updateIndex="jump" />
                    </div>
                    <ProjectButton v-if="Object.keys(button).length" :button="button" class="button" />
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss">
.block-blog-three-up {
    
    background-color: var(--cream);
    color: var(--teal);

    @media (min-width: $tablet) {
        background-image: url('@/assets/img/backgrounds/bubble-cream-3.png');
        background-position: right top;
        background-repeat: no-repeat;
    }

    &.is-post {
        background-color: white;
    }

    .container {
        padding: var(--box-padding-tb-m) 0;

        @media (min-width: $tablet) {
            padding: calc(var(--box-padding-tb) * 3) 0;
        }
    }

    .header {
        display: flex;
        width: 100%;
        align-items: center;
        margin-bottom: var(--box-padding-tb);

        a {
            margin-left: auto;
            display: none;

            @media (min-width: $tablet) {
                display: block;
            }
        }

        .headline {
            @include no-text-crop;
            padding: var(--box-padding-tb-m) 0;
        }
    }

    .posts {

        margin-bottom: var(--box-padding-tb);

        ul {
            display: flex;
            overflow-x: scroll;
            gap: var(--box-padding-tb);
            @include hide-scrollbar;
            scroll-snap-type: x mandatory;

            li {
                scroll-snap-align: start;
                flex: 1 1 calc(100%);
                flex-shrink: 0;
                color: var(--teal);
            }

            @media (min-width: $tablet) {
                li {
                    flex: 1 1 calc(33.333% - var(--box-padding-tb));
                    flex-shrink: 0;
                }
            }
        }
    }

    .controls {
        @media (min-width: $tablet) {
            display: none;
        }
    }

    .controller {
        display: flex;
        align-items: center;
        width: 100%;
        margin-bottom: var(--box-padding-tb);

        .dot-controls {
            margin-left: auto;
        }

        .back-next {
            .next svg path,
            .previous svg path {
                fill: var(--teal);
            }
        }
    }

}
</style>
